import React, { useState, useEffect, useRef } from 'react'
import {
  TextField,
  MenuItem,
  CircularProgress,
  InputAdornment,
  Popper,
  Paper,
  ClickAwayListener
} from '@mui/material'
import { Search, Clear } from '@mui/icons-material'
import { ComponentProps } from '../../../components/_WMS/FilterComponent/types'
import { createFilterValueWithDisplay } from '../../../types/filters'
import { ApiConfig, apiCall } from '../../../config/api'

interface UserAlias {
  userId: number
  userAlias: string
}

export const UserAutoCompleter: React.FC<ComponentProps> = ({
  value,
  onChange,
  fullWidth = true,
  transformForBackend,
  transformForDisplay
}) => {
  const [searchText, setSearchText] = useState('')
  const [selected, setSelected] = useState<UserAlias | null>(null)
  const [options, setOptions] = useState<UserAlias[]>([])
  const [loading, setLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()
  const anchorRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (searchText.trim().length < 3) {
      setOptions([])
      return
    }

    debounceRef.current = setTimeout(() => {
      setLoading(true)
      apiCall('GET', `${ApiConfig.UserApi}/GetByUserAlias?userAlias=${searchText.trim()}`)
        .then((response) => setOptions(response.data || []))
        .catch(() => setOptions([]))
        .finally(() => setLoading(false))
    }, 300)

    return () => clearTimeout(debounceRef.current)
  }, [searchText])

  useEffect(() => {
    if (!value && selected) {
      setSelected(null)
      setSearchText('')
      setOptions([])
    }
  }, [value])

  const handleSelect = (user: UserAlias) => {
    setSelected(user)
    setIsOpen(false)

    let eventValue = ''
    if (transformForBackend && transformForDisplay) {
      eventValue = createFilterValueWithDisplay(user.userId, user.userAlias)
    } else {
      eventValue = user.userId.toString()
    }

    onChange({
      target: { value: eventValue, name: 'userId' }
    } as React.ChangeEvent<HTMLInputElement>)
  }

  const handleClear = () => {
    setSelected(null)
    setSearchText('')
    setOptions([])
    onChange({ target: { value: '', name: 'userId' } } as React.ChangeEvent<HTMLInputElement>)
  }

  const displayValue = selected ? selected.userAlias : searchText

  return (
    <ClickAwayListener onClickAway={() => setIsOpen(false)}>
      <div ref={anchorRef} style={{ position: 'relative', width: fullWidth ? '100%' : 300 }}>
        <TextField
          value={displayValue}
          onChange={(e) => { setSearchText(e.target.value); setIsOpen(true) }}
          onFocus={() => setIsOpen(true)}
          variant='outlined'
          size='small'
          placeholder='Buscar Usuario...'
          autoComplete='off'
          fullWidth
          sx={{
            height: '44px',
            '& .MuiInputBase-root': { height: '44px' }
          }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position='end'>
                  {loading ? (
                    <CircularProgress size={20} />
                  ) : selected ? (
                    <Clear style={{ cursor: 'pointer' }} onClick={handleClear} />
                  ) : (
                    <Search />
                  )}
                </InputAdornment>
              )
            }
          }}
        />

        <Popper
          open={isOpen}
          anchorEl={anchorRef.current}
          placement='bottom-start'
          style={{ width: anchorRef.current?.offsetWidth || 'auto', zIndex: 1300 }}
        >
          <Paper elevation={3} style={{ maxHeight: 300, overflow: 'auto' }}>
            {searchText.length > 0 && searchText.trim().length < 3 && (
              <MenuItem disabled>Escriba al menos 3 caracteres</MenuItem>
            )}
            {searchText.trim().length >= 3 && !loading && options.length === 0 && (
              <MenuItem disabled>No se encontraron resultados</MenuItem>
            )}
            {options.map((user) => (
              <MenuItem key={user.userId} onClick={() => handleSelect(user)} style={{ cursor: 'pointer' }}>
                {user.userAlias}
              </MenuItem>
            ))}
          </Paper>
        </Popper>
      </div>
    </ClickAwayListener>
  )
}
